import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Grid, 
  TextField, 
  MenuItem, 
  Paper,
  useMediaQuery,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Divider,
  SelectChangeEvent,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Card,
  CardContent
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { GridItem } from '../types/mui-types';
import { 
  isValidZipCode, 
  isInServiceArea, 
  itemTypes, 
  calculatePrice,
  itemCategories,
  calculateSubItemPrice
} from '../data/pricingData';

const Home: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // State for zip code input
  const [zipCode, setZipCode] = useState<string>('');
  const [zipCodeError, setZipCodeError] = useState<string>('');
  
  // State for item selection
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedSubItem, setSelectedSubItem] = useState<string>('');
  const [subItems, setSubItems] = useState<any[]>([]);
  const [quantity, setQuantity] = useState<number>(1);
  
  // State for cart items
  const [cartItems, setCartItems] = useState<Array<{
    id: string;
    categoryId: string;
    category: string;
    name: string;
    description: string;
    quantity: number;
    price: number;
  }>>([]);
  
  // State for price calculation
  const [isCalculating, setIsCalculating] = useState<boolean>(false);
  const [priceDetails, setPriceDetails] = useState<{
    basePrice: number;
    serviceFee: number;
    tax: number;
    total: number;
  } | null>(null);
  
  // Update available sub-items when category changes
  useEffect(() => {
    if (selectedCategory) {
      const category = itemCategories.find(cat => cat.id === selectedCategory);
      if (category) {
        setSubItems(category.subItems);
        setSelectedSubItem(''); // Reset sub-item selection when category changes
      } else {
        setSubItems([]);
      }
    } else {
      setSubItems([]);
    }
  }, [selectedCategory]);
  
  // Handle zip code input change
  const handleZipCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // Only allow digits and limit to 5 characters
    if (/^\d*$/.test(value) && value.length <= 5) {
      setZipCode(value);
      setZipCodeError('');
    }
  };
  
  // Handle category selection change
  const handleCategoryChange = (event: SelectChangeEvent<string>) => {
    setSelectedCategory(event.target.value);
    // Reset price details when category changes
    setPriceDetails(null);
  };
  
  // Handle sub-item selection change
  const handleSubItemChange = (event: SelectChangeEvent<string>) => {
    setSelectedSubItem(event.target.value);
    // Reset price details when sub-item changes
    setPriceDetails(null);
  };
  
  // Calculate price based on inputs
  const calculateItemPrice = () => {
    // Validate zip code
    if (!zipCode) {
      setZipCodeError('Please enter a zip code');
      return;
    }
    
    if (!isValidZipCode(zipCode)) {
      setZipCodeError('Please enter a valid 5-digit zip code');
      return;
    }
    
    if (!isInServiceArea(zipCode)) {
      setZipCodeError('Sorry, we do not service this area yet');
      return;
    }
    
    // Validate cart has items
    if (cartItems.length === 0) {
      return;
    }
    
    // Simulate API call with loading state
    setIsCalculating(true);
    
    // Simulate network delay
    setTimeout(() => {
      // Calculate price for all items in cart
      let basePrice = 0;
      
      // Add up the price for each item based on quantity
      cartItems.forEach(item => {
        basePrice += item.price * item.quantity;
      });
      
      const serviceFee = Math.round(basePrice * 0.15); // 15% service fee
      const subtotal = basePrice + serviceFee;
      const tax = Math.round(subtotal * 0.085); // 8.5% tax
      const total = subtotal + tax;
      
      setPriceDetails({
        basePrice,
        serviceFee,
        tax,
        total
      });
      
      setIsCalculating(false);
    }, 1000);
  };
  
  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };
  
  // Add item to cart
  const handleAddToCart = () => {
    if (selectedCategory && selectedSubItem) {
      const category = itemCategories.find(cat => cat.id === selectedCategory);
      const subItem = subItems.find(item => item.id === selectedSubItem);
      if (category && subItem) {
        const newItem = {
          id: subItem.id,
          categoryId: selectedCategory,
          category: category.name,
          name: subItem.name,
          description: subItem.description,
          quantity: quantity,
          price: calculateSubItemPrice(zipCode, selectedCategory, selectedSubItem)
        };
        setCartItems([...cartItems, newItem]);
        
        // Reset form fields to default values
        setSelectedCategory('');
        setSelectedSubItem('');
        setQuantity(1);
      }
    }
  };
  
  // Remove item from cart
  const handleRemoveFromCart = (id: string) => {
    setCartItems(cartItems.filter(item => item.id !== id));
  };
  
  return (
    <Box>
      {/* Hero Section */}
      <Box sx={{ 
        bgcolor: 'primary.main', 
        color: 'white', 
        py: { xs: 6, md: 10 },
        backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(https://images.pexels.com/photos/1152359/pexels-photo-1152359.jpeg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        }}
      >
        <Container maxWidth="lg">
          <GridItem container spacing={4} alignItems="center">
            <GridItem item xs={12} md={6}>
              <Typography 
                variant={isMobile ? "h4" : "h3"} 
                component="h1" 
                gutterBottom
                sx={{ fontWeight: 'bold' }}
              >
                Professional Junk Removal Services
              </Typography>
              <Typography variant="h6" paragraph>
                Fast, reliable, and eco-friendly junk removal for your home or business
              </Typography>
              <Button 
                variant="contained" 
                color="secondary" 
                size="large"
                sx={{ 
                  mt: 2,
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  py: 1.5,
                  px: 4
                }}
              >
                Get a Quote
              </Button>
            </GridItem>
            <GridItem item xs={12} md={6}>
              <Paper 
                elevation={3} 
                sx={{ 
                  p: 3, 
                  borderRadius: 2,
                  bgcolor: 'rgba(255, 255, 255, 0.9)'
                }}
              >
                <Typography 
                  variant="h5" 
                  component="h2" 
                  gutterBottom 
                  color="primary.main"
                  sx={{ fontWeight: 'bold' }}
                >
                  Get a Price Estimate
                </Typography>
                
                <GridItem container spacing={2}>
                  <GridItem item xs={12}>
                    <TextField
                      fullWidth
                      label="Enter Zip Code"
                      variant="outlined"
                      value={zipCode}
                      onChange={handleZipCodeChange}
                      error={!!zipCodeError}
                      helperText={zipCodeError}
                      placeholder="e.g. 10001"
                      sx={{
                        '& .MuiInputBase-root': {
                          height: '56px'
                        }
                      }}
                      InputProps={{
                        sx: { 
                          borderRadius: 1,
                          '& fieldset': {
                            borderColor: 'rgba(0, 0, 0, 0.23)',
                          },
                          '&:hover fieldset': {
                            borderColor: 'black',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'black',
                          },
                        }
                      }}
                    />
                  </GridItem>
                  
                  <GridItem item xs={12}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel id="category-select-label">Select Item Category</InputLabel>
                      <Select
                        labelId="category-select-label"
                        id="category-select"
                        value={selectedCategory}
                        onChange={handleCategoryChange}
                        label="Select Item Category"
                        sx={{ 
                          borderRadius: 1,
                          height: '56px',
                          '& .MuiSelect-select': {
                            paddingTop: '12px',
                            paddingBottom: '12px',
                            minWidth: '240px',
                            whiteSpace: 'normal'
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(0, 0, 0, 0.23)'
                          },
                          '& .MuiSelect-icon': {
                            right: '8px'
                          }
                        }}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 300,
                              width: 'auto',
                              minWidth: '240px'
                            }
                          }
                        }}
                      >
                        <MenuItem value="">
                          <em>Select a category</em>
                        </MenuItem>
                        {itemCategories.map((category) => (
                          <MenuItem key={category.id} value={category.id}>
                            {category.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </GridItem>
                  
                  {selectedCategory && (
                    <GridItem item xs={12}>
                      <FormControl fullWidth variant="outlined" disabled={!selectedCategory}>
                        <InputLabel id="sub-item-select-label">Select Specific Item</InputLabel>
                        <Select
                          labelId="sub-item-select-label"
                          id="sub-item-select"
                          value={selectedSubItem}
                          onChange={handleSubItemChange}
                          label="Select Specific Item"
                          sx={{ 
                            borderRadius: 1,
                            height: '56px',
                            '& .MuiSelect-select': {
                              paddingTop: '12px',
                              paddingBottom: '12px',
                              minWidth: '240px',
                              whiteSpace: 'normal'
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(0, 0, 0, 0.23)'
                            },
                            '& .MuiSelect-icon': {
                              right: '8px'
                            }
                          }}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 300,
                                width: 'auto',
                                minWidth: '240px'
                              }
                            }
                          }}
                        >
                          <MenuItem value="">
                            <em>Select a specific item</em>
                          </MenuItem>
                          {subItems.map((item) => (
                            <MenuItem key={item.id} value={item.id}>
                              {item.name} - {item.description}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </GridItem>
                  )}
                  
                  {selectedCategory && selectedSubItem && (
                    <GridItem item xs={12}>
                      <TextField
                        fullWidth
                        label="Quantity"
                        variant="outlined"
                        value={quantity}
                        onChange={(event) => setQuantity(parseInt(event.target.value))}
                        type="number"
                        sx={{
                          '& .MuiInputBase-root': {
                            height: '56px'
                          }
                        }}
                        InputProps={{
                          sx: { 
                            borderRadius: 1,
                            '& fieldset': {
                              borderColor: 'rgba(0, 0, 0, 0.23)',
                            },
                            '&:hover fieldset': {
                              borderColor: 'black',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: 'black',
                            },
                          }
                        }}
                      />
                    </GridItem>
                  )}
                  
                  <GridItem item xs={12}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      size="large"
                      onClick={handleAddToCart}
                      disabled={!zipCode || !selectedCategory || !selectedSubItem}
                      sx={{ 
                        height: '56px',
                        fontWeight: 'bold',
                        mt: 0
                      }}
                    >
                      Add to Truck
                    </Button>
                  </GridItem>
                </GridItem>
                
                {cartItems.length > 0 && (
                  <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Truck Items
                    </Typography>
                    
                    <List>
                      {cartItems.map((item) => (
                        <ListItem key={item.id}>
                          <ListItemText 
                            primary={
                              <Typography>
                                <strong>{item.quantity}x</strong> {item.name}
                              </Typography>
                            } 
                            secondary={
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  {item.description}
                                </Typography>
                                <Typography variant="body2" color="primary.main" sx={{ fontWeight: 'bold' }}>
                                  {formatCurrency(item.price * item.quantity)}
                                </Typography>
                              </Box>
                            } 
                          />
                          <ListItemSecondaryAction>
                            <IconButton edge="end" aria-label="delete" onClick={() => handleRemoveFromCart(item.id)}>
                              <DeleteIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                    
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      size="large"
                      onClick={calculateItemPrice}
                      disabled={isCalculating}
                      sx={{ 
                        height: '56px',
                        fontWeight: 'bold',
                        mt: 2
                      }}
                    >
                      {isCalculating ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        'Calculate Price'
                      )}
                    </Button>
                  </Box>
                )}
                
                {priceDetails && (
                  <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Price Estimate
                    </Typography>
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                        Items:
                      </Typography>
                      {cartItems.map((item) => (
                        <GridItem container spacing={1} key={item.id}>
                          <GridItem item xs={6}>
                            <Typography variant="body2">
                              {item.name} x{item.quantity}
                            </Typography>
                          </GridItem>
                          <GridItem item xs={6}>
                            <Typography variant="body2" align="right">
                              {formatCurrency(item.price * item.quantity)}
                            </Typography>
                          </GridItem>
                        </GridItem>
                      ))}
                    </Box>
                    
                    <Divider sx={{ my: 1 }} />
                    
                    <GridItem container spacing={1}>
                      <GridItem item xs={8}>
                        <Typography variant="body1">Base Price:</Typography>
                      </GridItem>
                      <GridItem item xs={4}>
                        <Typography variant="body1" align="right">
                          {formatCurrency(priceDetails.basePrice)}
                        </Typography>
                      </GridItem>
                      
                      <GridItem item xs={8}>
                        <Typography variant="body1">Service Fee (15%):</Typography>
                      </GridItem>
                      <GridItem item xs={4}>
                        <Typography variant="body1" align="right">
                          {formatCurrency(priceDetails.serviceFee)}
                        </Typography>
                      </GridItem>
                      
                      <GridItem item xs={8}>
                        <Typography variant="body1">Tax (8.5%):</Typography>
                      </GridItem>
                      <GridItem item xs={4}>
                        <Typography variant="body1" align="right">
                          {formatCurrency(priceDetails.tax)}
                        </Typography>
                      </GridItem>
                      
                      <GridItem item xs={8}>
                        <Typography variant="body1" sx={{ fontWeight: 'bold', mt: 1 }}>Total:</Typography>
                      </GridItem>
                      <GridItem item xs={4}>
                        <Typography variant="body1" align="right" sx={{ fontWeight: 'bold', mt: 1 }}>
                          {formatCurrency(priceDetails.total)}
                        </Typography>
                      </GridItem>
                    </GridItem>
                    
                    <Button
                      fullWidth
                      variant="contained"
                      color="secondary"
                      size="large"
                      sx={{ mt: 2 }}
                    >
                      Book Now
                    </Button>
                  </Box>
                )}
              </Paper>
            </GridItem>
          </GridItem>
        </Container>
      </Box>
      
      {/* How It Works Section */}
      <Box sx={{ py: 5 }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            component="h2" 
            align="center" 
            gutterBottom
            sx={{ fontWeight: 'bold' }}
          >
            How It Works
          </Typography>
          <Typography 
            variant="h6" 
            align="center" 
            color="text.secondary" 
            paragraph
            sx={{ mb: 3 }}
          >
            Our simple 3-step process makes junk removal hassle-free
          </Typography>
          
          <GridItem container spacing={3}>
            {[
              {
                title: '1. Book',
                description: 'Schedule your junk removal service online or by phone. Choose a date and time that works for you.'
              },
              {
                title: '2. We Arrive',
                description: 'Our professional team arrives on time, ready to remove your unwanted items quickly and efficiently.'
              },
              {
                title: '3. We Haul',
                description: 'We load everything into our truck and clean up the area. We recycle and donate items whenever possible.'
              }
            ].map((step, index) => (
              <GridItem item xs={12} md={4} key={index}>
                <Paper 
                  elevation={2} 
                  sx={{ 
                    p: 2, 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    borderRadius: 2,
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-5px)'
                    }
                  }}
                >
                  <Typography 
                    variant="h5" 
                    component="h3" 
                    gutterBottom 
                    color="primary.main"
                    sx={{ fontWeight: 'bold' }}
                  >
                    {step.title}
                  </Typography>
                  <Typography variant="body2">
                    {step.description}
                  </Typography>
                </Paper>
              </GridItem>
            ))}
          </GridItem>
        </Container>
      </Box>
      
      {/* Services Section */}
      <Box sx={{ py: 5, bgcolor: '#f8f8f8' }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            component="h2" 
            align="center" 
            gutterBottom
            sx={{ fontWeight: 'bold', mb: 3 }}
          >
            Our Services
          </Typography>
          <GridItem container spacing={3}>
            <GridItem item container spacing={3} xs={12} md={6}>
              {/* Left column */}
              <GridItem item xs={12}>
                <Paper 
                  elevation={2} 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    borderRadius: 2,
                    overflow: 'hidden',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-5px)'
                    }
                  }}
                >
                  <Box 
                    sx={{ 
                      height: 200, 
                      backgroundImage: `url(https://cleanstartbc.ca/wp-content/uploads/2023/04/cluttered-garage.jpg)`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat'
                    }} 
                  />
                  <Box sx={{ p: 2, flexGrow: 1 }}>
                    <Typography 
                      variant="h6" 
                      component="h3" 
                      gutterBottom 
                      sx={{ fontWeight: 'bold', mb: 1 }}
                    >
                      Residential Junk Removal
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      We help homeowners clear out unwanted items from their homes, garages, and yards.
                    </Typography>
                  </Box>
                </Paper>
              </GridItem>
              <GridItem item xs={12}>
                <Paper 
                  elevation={2} 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    borderRadius: 2,
                    overflow: 'hidden',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-5px)'
                    }
                  }}
                >
                  <Box 
                    sx={{ 
                      height: 200, 
                      backgroundImage: `url(https://images.pexels.com/photos/276583/pexels-photo-276583.jpeg)`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat'
                    }} 
                  />
                  <Box sx={{ p: 2, flexGrow: 1 }}>
                    <Typography 
                      variant="h6" 
                      component="h3" 
                      gutterBottom 
                      sx={{ fontWeight: 'bold', mb: 1 }}
                    >
                      Furniture Removal
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Quick and easy removal of old furniture, sofas, tables, chairs, and more.
                    </Typography>
                  </Box>
                </Paper>
              </GridItem>
            </GridItem>
            
            <GridItem item container spacing={3} xs={12} md={6}>
              {/* Right column */}
              <GridItem item xs={12}>
                <Paper 
                  elevation={2} 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    borderRadius: 2,
                    overflow: 'hidden',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-5px)'
                    }
                  }}
                >
                  <Box 
                    sx={{ 
                      height: 200, 
                      backgroundImage: `url(https://samsjunkremoval.ca/wp-content/uploads/2017/04/Cheap-Junk-Removal-Services.jpg)`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat'
                    }} 
                  />
                  <Box sx={{ p: 2, flexGrow: 1 }}>
                    <Typography 
                      variant="h6" 
                      component="h3" 
                      gutterBottom 
                      sx={{ fontWeight: 'bold', mb: 1 }}
                    >
                      Commercial Junk Removal
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Office cleanouts, retail space clearing, and construction debris removal for businesses.
                    </Typography>
                  </Box>
                </Paper>
              </GridItem>
              <GridItem item xs={12}>
                <Paper 
                  elevation={2} 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    borderRadius: 2,
                    overflow: 'hidden',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-5px)'
                    }
                  }}
                >
                  <Box 
                    sx={{ 
                      height: 200, 
                      backgroundImage: `url(https://www.1800gotjunk.com/sites/default/files/GJ_March%20Blog_Appliances_Blog%20Image%2002.jpg)`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat'
                    }} 
                  />
                  <Box sx={{ p: 2, flexGrow: 1 }}>
                    <Typography 
                      variant="h6" 
                      component="h3" 
                      gutterBottom 
                      sx={{ fontWeight: 'bold', mb: 1 }}
                    >
                      Appliance Removal
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Removal of refrigerators, washers, dryers, and other household appliances.
                    </Typography>
                  </Box>
                </Paper>
              </GridItem>
            </GridItem>
          </GridItem>
        </Container>
      </Box>
      
      {/* Call to Action */}
      <Box sx={{ 
        py: 10, 
        bgcolor: 'primary.main', 
        color: 'white',
        backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url(https://images.pexels.com/photos/1152359/pexels-photo-1152359.jpeg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}>
        <Container maxWidth="md">
          <Typography 
            variant="h3" 
            component="h2" 
            align="center" 
            gutterBottom
            sx={{ fontWeight: 'bold' }}
          >
            Ready to Get Started?
          </Typography>
          <Typography variant="h6" align="center" paragraph>
            Book your junk removal service today and reclaim your space!
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Button 
              variant="contained" 
              color="secondary" 
              size="large"
              sx={{ 
                py: 1.5,
                px: 4,
                fontWeight: 'bold',
                fontSize: '1.1rem'
              }}
            >
              Book Now
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;
